'use client'

import { memo } from 'react'
import { formatSingleDate } from '~/lib/date-utils'
import { formatPrice } from '~/utils/format'

interface BookingSummaryProps {
  arrivalDate: string
  departureDate: string
  timeIn: string
  timeOut: string
  nights: number
  roomCount: number
  discountAmount: number
  subAmount: number
  totalAmount: number
  t: (key: string) => string
}

const BookingSummary = ({
  arrivalDate,
  departureDate,
  timeIn,
  timeOut,
  nights,
  roomCount,
  discountAmount,
  subAmount,
  totalAmount,
  t,
}: BookingSummaryProps) => {
  return (
    <div>
      <div className="rounded-[6px] bg-[#F3F3F3] p-3 text-[12px] text-[#2D2E32]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <label className="text-[12px] font-light sm:text-[16px]">{t('booking.checkIn')}</label>
            <span className="text-[12px] font-[400] sm:text-[16px]">
              {formatSingleDate(arrivalDate)}
            </span>
            <span className="text-[12px] font-light sm:text-[16px]">
              {t('booking.from')} {timeIn}
            </span>
          </div>
          <div className="h-[1px] w-[9%] bg-[#3A3B3F]" />
          <div className="flex flex-col text-right">
            <label className="text-[12px] font-light sm:text-[16px]">{t('booking.checkOut')}</label>
            <span className="text-[12px] font-medium sm:text-[16px]">
              {formatSingleDate(departureDate)}
            </span>
            <span className="text-[12px] font-light sm:text-[16px]">
              {t('booking.before')} {timeOut}
            </span>
          </div>
        </div>

        <div className="mt-2 flex justify-between border-t border-[#CED0D3] pt-3">
          <div className="flex flex-1 flex-col items-start">
            <label className="text-[12px] font-light sm:text-[16px]">
              {t('booking.totalStay')}
            </label>
            <span className="text-[12px] font-[400] sm:text-[16px]">
              {String(nights)?.padStart(2, '0')} {t('booking.nights')}
            </span>
          </div>
          <div className="h-[21px] w-[1px] bg-[#CED0D3]" />
          <div className="flex flex-1 flex-col items-end">
            <label className="text-[12px] font-light sm:text-[16px]">
              {t('booking.totalRooms')}
            </label>
            <span className="text-[12px] font-[400] sm:text-[16px]">
              {String(roomCount).padStart(2, '0')} {t('booking.rooms')}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-10 w-full text-[12px] sm:pt-0">
        <h3 className="font-lora text-[16px] text-base font-medium text-[#2D2E32] sm:text-[20px]">
          {t('booking.paymentInfo')}
        </h3>
        <div className="flex justify-between pt-3 sm:pb-1">
          <label className="text-[12px] font-light text-[#2D2E32] sm:text-[14px]">
            {t('booking.roomPrice')} (x{roomCount})
          </label>
          <span className="text-[12px] font-medium sm:text-[14px]">{formatPrice(subAmount)}</span>
        </div>
        <div className="my-3 h-[1px] bg-[#F3F3F3] sm:hidden" />
        <div className="flex items-center justify-between text-[18px] font-medium sm:mt-2">
          <label className="text-[12px] font-light text-[#666B74] sm:text-[14px]">
            <span className="text-[#2D2E32]">{t('booking.voucher_code')}</span>
          </label>
          <span className="text-[12px] font-medium sm:text-[14px]">
            {discountAmount ? `-${formatPrice(discountAmount)}` : 0}
          </span>
        </div>
        <div className="my-3 h-[1px] bg-[#F3F3F3] sm:hidden" />
        <div className="flex items-center justify-between text-[18px] font-medium text-[#896145] sm:mt-2">
          <label className="text-[12px] font-light text-[#666B74] sm:text-[14px]">
            <span className="text-[#2D2E32]">{t('booking.total')}</span> ({t('booking.taxIncluded')}
            )
          </label>
          <span className="text-[18px] font-medium sm:text-[24px] sm:leading-none">
            {formatPrice(totalAmount)}
          </span>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white p-4 sm:hidden">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="font-lexend text-lg font-medium text-secondary-950 md:leading-[36px]">
              {formatPrice(totalAmount)}
            </span>
            <span className="font-lexend text-[12px] text-[#666B74]">
              {t('booking.total')} ({t('booking.taxIncluded')})
            </span>
          </div>
          <div className={`text-[12px] font-light leading-4 text-red-950`}>{t('payment.note')}</div>
        </div>
      </div>
      <div className={`mt-3 text-[10px] font-light leading-4 text-red-950 md:text-[12px]`}>
        {t('payment.note')}
      </div>
    </div>
  )
}

function arePropsEqual(prevProps: BookingSummaryProps, nextProps: BookingSummaryProps) {
  return (
    prevProps.arrivalDate === nextProps.arrivalDate &&
    prevProps.departureDate === nextProps.departureDate &&
    prevProps.timeIn === nextProps.timeIn &&
    prevProps.timeOut === nextProps.timeOut &&
    prevProps.nights === nextProps.nights &&
    prevProps.roomCount === nextProps.roomCount &&
    prevProps.totalAmount === nextProps.totalAmount &&
    prevProps.discountAmount === nextProps.discountAmount
  )
}

export default memo(BookingSummary, arePropsEqual)
